import { useEffect, useState, useCallback } from "react";
import { adminSystemHealth, adminEnvAudit, adminRunPaymentWatchdog } from "@/api";
import { toast } from "sonner";
import {
  Activity, RefreshCw, Loader2, Webhook, ShieldCheck, ShieldAlert, AlertTriangle,
  CheckCircle2, XCircle, Info, Play, Eye, Copy, GitCommit, Server, ChevronDown, ChevronUp, Radar,
} from "lucide-react";
import { Button } from "@/components/ui/button";

// ---------- helpers ----------
function timeAgo(iso) {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff)) return null;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

function webhookHealth(hb) {
  if (!hb?.at) return { tone: "gray", label: "Never received", hint: "No webhook has reached this backend yet. Check the URL in the provider dashboard." };
  const ageH = (Date.now() - new Date(hb.at).getTime()) / 3600e3;
  if (hb.signature_ok === false) return { tone: "red", label: "Signature failed", hint: "Last webhook was rejected — the webhook secret in the provider dashboard doesn't match the backend." };
  if (ageH < 24) return { tone: "green", label: "Healthy", hint: "Webhooks are arriving." };
  if (ageH < 24 * 7) return { tone: "amber", label: "Quiet", hint: "No webhook in the last 24h — fine if there were no payments, otherwise check the dashboard URL." };
  return { tone: "red", label: "Stale", hint: "No webhook in over a week. Verify the webhook URL + secret." };
}

const TONE = {
  green: { dot: "bg-emerald-500", pill: "bg-emerald-50 text-emerald-700 border-emerald-200", ring: "ring-emerald-500/20" },
  amber: { dot: "bg-amber-500", pill: "bg-amber-50 text-amber-700 border-amber-200", ring: "ring-amber-500/20" },
  red: { dot: "bg-red-500", pill: "bg-red-50 text-red-700 border-red-200", ring: "ring-red-500/20" },
  gray: { dot: "bg-gray-400", pill: "bg-gray-50 text-gray-600 border-gray-200", ring: "ring-gray-400/20" },
};

const STATUS_ICON = {
  ok: { icon: CheckCircle2, cls: "text-emerald-600" },
  warn: { icon: AlertTriangle, cls: "text-amber-600" },
  fail: { icon: XCircle, cls: "text-red-600" },
  info: { icon: Info, cls: "text-gray-400" },
};

function copy(text) {
  navigator.clipboard?.writeText(text).then(() => toast.success("Copied")).catch(() => {});
}

// ---------- sub-components ----------
function WebhookBadge({ name, hb, url, testId }) {
  const h = webhookHealth(hb);
  const t = TONE[h.tone];
  return (
    <div data-testid={testId} data-tone={h.tone} className="rounded-xl border border-gray-200 p-4 bg-white">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Webhook className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-semibold text-[#0A2540]">{name}</span>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-xs font-semibold ${t.pill}`} data-testid={`${testId}-status`}>
          <span className={`h-2 w-2 rounded-full ${t.dot} ${h.tone === "green" ? "animate-pulse" : ""}`} />
          {h.label}
        </span>
      </div>
      <div className="mt-3">
        <p className="text-xs text-gray-500">Last webhook received</p>
        <p className="text-sm font-semibold text-[#0A2540]" data-testid={`${testId}-last`}>
          {hb?.at ? <>{timeAgo(hb.at)} <span className="font-normal text-gray-400">· {fmt(hb.at)}</span></> : "—"}
        </p>
        {hb?.event && <p className="text-xs text-gray-500 mt-0.5">Event: <span className="font-mono">{hb.event}</span>{hb.env ? ` · ${hb.env}` : ""}</p>}
      </div>
      <p className="text-xs text-gray-500 mt-2 leading-relaxed">{h.hint}</p>
      {url && (
        <button onClick={() => copy(url)} className="mt-2 w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 text-left transition-colors">
          <span className="text-[11px] font-mono text-gray-600 truncate">{url}</span>
          <Copy className="h-3.5 w-3.5 text-gray-400 shrink-0" />
        </button>
      )}
    </div>
  );
}

function Stat({ label, value, tone = "text-[#0A2540]", testId }) {
  return (
    <div className="rounded-lg bg-gray-50 px-3 py-2 text-center">
      <p className={`text-lg font-black ${tone}`} data-testid={testId}>{value ?? "—"}</p>
      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</p>
    </div>
  );
}

// ---------- main panel ----------
export default function SystemHealthPanel() {
  const [health, setHealth] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [auditLoading, setAuditLoading] = useState(false);
  const [running, setRunning] = useState(null); // 'dry' | 'live' | null
  const [lastRun, setLastRun] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const loadHealth = useCallback(async () => {
    try {
      const res = await adminSystemHealth();
      setHealth(res.data);
    } catch (e) {
      toast.error("Failed to load system health");
    }
  }, []);

  const loadAudit = useCallback(async (live = false) => {
    setAuditLoading(true);
    try {
      const res = await adminEnvAudit(live);
      setAudit(res.data);
      if (live) toast.success("Live connectivity checks complete");
    } catch (e) {
      toast.error("Env audit failed");
    } finally { setAuditLoading(false); }
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadHealth(), loadAudit(false)]);
    setLoading(false);
  }, [loadHealth, loadAudit]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  const runWatchdog = async (dryRun) => {
    if (!dryRun && !window.confirm("Run the Payment Watchdog now? Any captured-but-pending subscriptions will be activated.")) return;
    setRunning(dryRun ? "dry" : "live");
    try {
      const res = await adminRunPaymentWatchdog(dryRun);
      const s = res.data.summary || {};
      setLastRun(s);
      if (s.skipped) toast.info("Watchdog is already running");
      else toast.success(dryRun
        ? `Preview: ${s.activated} would be activated, ${s.duplicates || 0} duplicate(s), ${s.errors} error(s)`
        : `Watchdog done: ${s.activated} activated, ${s.duplicates || 0} duplicate(s), ${s.errors} error(s)`);
      if (!dryRun) loadHealth();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Watchdog run failed");
    } finally { setRunning(null); }
  };

  const wd = health?.payment_watchdog;
  const build = health?.build || audit?.build;
  const overall = audit?.overall;
  const overallTone = overall === "fail" ? "red" : overall === "warn" ? "amber" : overall === "ok" ? "green" : "gray";
  const visibleChecks = (audit?.checks || []).filter(c => showAll || c.status === "fail" || c.status === "warn");

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="system-health-panel">
      <div className="px-6 py-4 border-b bg-slate-50 border-slate-200 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-slate-700">
          <Activity className="h-5 w-5" />
          <div>
            <h3 className="font-bold text-sm">System Health</h3>
            <p className="text-xs opacity-80">Webhooks, payment watchdog and environment configuration</p>
          </div>
        </div>
        <Button onClick={refreshAll} disabled={loading} size="sm" variant="outline" className="rounded-lg" data-testid="system-health-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          <span className="ml-1.5">Refresh</span>
        </Button>
      </div>

      {loading && !health ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#1565C0]" /></div>
      ) : (
        <div className="p-6 space-y-6">
          {/* Build strip */}
          {build && (
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-gray-600" data-testid="system-build-info">
              <span className="inline-flex items-center gap-1.5"><Server className="h-3.5 w-3.5 text-gray-400" /> v{build.app_version} · {build.host}</span>
              <span className="inline-flex items-center gap-1.5"><GitCommit className="h-3.5 w-3.5 text-gray-400" /> {build.commit || "commit unknown"}{build.branch ? ` (${build.branch})` : ""}</span>
              <span>Up since {fmt(build.started_at)}</span>
            </div>
          )}

          {/* Webhook health */}
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Webhook Health</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <WebhookBadge name="Razorpay" hb={health?.webhooks?.razorpay} url={health?.webhook_urls?.razorpay} testId="webhook-badge-razorpay" />
              <WebhookBadge name="Digio (DigiLocker)" hb={health?.webhooks?.digio} url={health?.webhook_urls?.digio} testId="webhook-badge-digio" />
            </div>
          </div>

          {/* Payment watchdog */}
          <div className="rounded-xl border border-gray-200 p-4" data-testid="payment-watchdog-card">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0"><Radar className="h-4 w-4 text-[#1565C0]" /></div>
                <div>
                  <p className="text-sm font-semibold text-[#0A2540]">Payment Watchdog</p>
                  <p className="text-xs text-gray-500">Daily at 2:30 AM IST — activates subscriptions whose Razorpay payment was captured but still show pending.</p>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="outline" className="rounded-lg" disabled={!!running} onClick={() => runWatchdog(true)} data-testid="watchdog-preview-btn">
                  {running === "dry" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}<span className="ml-1.5">Preview</span>
                </Button>
                <Button size="sm" className="rounded-lg bg-[#1565C0] hover:bg-[#0D47A1] text-white" disabled={!!running} onClick={() => runWatchdog(false)} data-testid="watchdog-run-btn">
                  {running === "live" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}<span className="ml-1.5">Run now</span>
                </Button>
              </div>
            </div>

            <div className="mt-4">
              <p className="text-xs text-gray-500">
                Last run: <span className="font-semibold text-[#0A2540]" data-testid="watchdog-last-run">{wd?.at ? `${timeAgo(wd.at)} · ${fmt(wd.at)}` : "never"}</span>
                {wd?.source && <> · {wd.source}</>}{wd?.env && <> · {wd.env}</>}
                {!health?.scheduler_enabled && <span className="ml-2 text-amber-700">(in-process scheduler OFF — needs external cron)</span>}
              </p>
              {wd && (
                <div className="grid grid-cols-4 gap-2 mt-2">
                  <Stat label="Checked" value={wd.checked} testId="watchdog-checked" />
                  <Stat label="Activated" value={wd.activated} tone="text-emerald-600" testId="watchdog-activated" />
                  <Stat label="Duplicates" value={wd.duplicates ?? 0} tone="text-amber-600" testId="watchdog-duplicates" />
                  <Stat label="Errors" value={wd.errors} tone={wd.errors ? "text-red-600" : "text-[#0A2540]"} testId="watchdog-errors" />
                </div>
              )}
              {wd?.error_samples?.length > 0 && (
                <p className="text-xs text-red-600 mt-2 break-all">Sample error: {wd.error_samples[0].error}</p>
              )}
            </div>

            {lastRun && !lastRun.skipped && (
              <div className="mt-4 rounded-lg bg-gray-50 p-3 text-xs space-y-2" data-testid="watchdog-run-result">
                <p className="font-semibold text-[#0A2540]">
                  {lastRun.dry_run ? "Preview result (nothing changed)" : "Run result"} — {lastRun.checked} checked in {lastRun.duration_ms} ms
                </p>
                {lastRun.activated_items?.map(i => (
                  <p key={i.subscription_id} className="text-emerald-700">
                    {lastRun.dry_run ? "Would activate" : "Activated"} · order <span className="font-mono">{i.order_id}</span> · {i.payment_id} · ₹{i.amount_paid}{i.underpaid ? " · UNDERPAID" : ""}
                  </p>
                ))}
                {lastRun.duplicate_items?.map(i => (
                  <p key={i.subscription_id} className="text-amber-700">
                    Duplicate payment (refund candidate) · order <span className="font-mono">{i.order_id}</span> · {i.payment_id} · ₹{i.amount_paid}
                  </p>
                ))}
                {!lastRun.activated_items?.length && !lastRun.duplicate_items?.length && !lastRun.errors && (
                  <p className="text-gray-500">No stuck payments found.</p>
                )}
                {lastRun.errors > 0 && <p className="text-red-600 break-all">{lastRun.errors} lookup error(s): {lastRun.error_samples?.[0]?.error}</p>}
              </div>
            )}
          </div>

          {/* Env audit */}
          <div className="rounded-xl border border-gray-200 p-4" data-testid="env-audit-card">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-slate-50 flex items-center justify-center shrink-0">
                  {overall === "ok" ? <ShieldCheck className="h-4 w-4 text-emerald-600" /> : <ShieldAlert className={`h-4 w-4 ${overall === "fail" ? "text-red-600" : "text-amber-600"}`} />}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-[#0A2540]">Environment Audit</p>
                    {overall && (
                      <span className={`px-2 py-0.5 rounded-full border text-[11px] font-bold uppercase ${TONE[overallTone].pill}`} data-testid="env-audit-overall">{overall}</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {audit ? <>{audit.summary.ok} ok · {audit.summary.warn} warning · {audit.summary.fail} failing</> : "—"} · values are masked
                  </p>
                </div>
              </div>
              <Button size="sm" variant="outline" className="rounded-lg shrink-0" disabled={auditLoading} onClick={() => loadAudit(true)} data-testid="env-audit-live-btn">
                {auditLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Radar className="h-3.5 w-3.5" />}<span className="ml-1.5">Run live checks</span>
              </Button>
            </div>

            <div className="mt-4 divide-y divide-gray-100" data-testid="env-audit-list">
              {visibleChecks.length === 0 && (
                <p className="text-xs text-emerald-700 py-2">No warnings or failures. 🎉</p>
              )}
              {visibleChecks.map((c, idx) => {
                const S = STATUS_ICON[c.status] || STATUS_ICON.info;
                const Icon = S.icon;
                return (
                  <div key={`${c.key}-${idx}`} className="flex items-start gap-3 py-2.5" data-testid={`env-check-${c.key}`} data-status={c.status}>
                    <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${S.cls}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-x-2">
                        <span className="text-xs font-mono font-semibold text-[#0A2540]">{c.key}</span>
                        <span className="text-[10px] uppercase tracking-wider text-gray-400">{c.group}</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5">{c.message}</p>
                      {c.value && <p className="text-[11px] font-mono text-gray-400 mt-0.5 break-all">{c.value}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
            {audit && (
              <button onClick={() => setShowAll(v => !v)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#1565C0] hover:underline" data-testid="env-audit-toggle">
                {showAll ? <><ChevronUp className="h-3.5 w-3.5" /> Show only issues</> : <><ChevronDown className="h-3.5 w-3.5" /> Show all {audit.checks.length} checks</>}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
